import React, { useState } from 'react';
import { Button, Grid2, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGetShows } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import ShowsTable from './components/ShowsTable';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../hooks/useCapabilities';

const ShowsPage = () => {
    const { can, CAPABILITIES } = useCapabilities();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { notifyError } = useNotify();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'ASC']);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetShows(
        {
            search,
            page,
            pageSize: limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    return (
        <PageWithTitle
            title={t('shows.title')}
            inlineHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    {can(CAPABILITIES.SHOWS_MANAGE) && (
                        <Grid2 sx={{ flexShrink: 0 }}>
                            <Tooltip title={t('shows.newShowTitle')}>
                                <Button
                                    onClick={() => {
                                        navigate('/shows/new');
                                    }}
                                    sx={{ minWidth: 0, height: 40, px: 2 }}
                                >
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                        </Grid2>
                    )}
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            <ShowsTable
                rows={data?.shows}
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                reloadData={reloadData}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
            />
        </PageWithTitle>
    );
};

export default ShowsPage;
